import { useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';

import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import {
  ALLOWED_PROCEDURAL_MIME_TYPES,
  MAX_PROCEDURAL_FILE_SIZE_BYTES,
} from '../../../../../../../../backend/src/constants/procedural-timeline-limits';
import { UploadEligibilityResponse } from '../../../../../../../../backend/src/matter/procedural-timeline/dto/upload-eligibility.response.dto';
import { ProceduralJurisdiction } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { useDeleteStagedProceduralFile } from '@/api/mutations/useDeleteStagedProceduralFile';
import { useStageProceduralFile } from '@/api/mutations/useStageProceduralFile';
import { useSubmitProceduralFile } from '@/api/mutations/useSubmitProceduralFile';
import { useGetProceduralTimelineEvents } from '@/api/queries/useGetProceduralTimelineEvents';
import { useGetProceduralTimelineUploadEligibility } from '@/api/queries/useGetProceduralTimelineUploadEligibility';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { detectTimeZone, getTimeZoneOptions } from '@/new/lib/time-zones';
import { getFrontendURLRegion } from '@/utils/checkFrontendURL';

const ACCEPT_ATTR =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const JURISDICTION_LABELS: Record<ProceduralJurisdiction, string> = {
  [ProceduralJurisdiction.EnglandAndWales]: 'England & Wales',
  [ProceduralJurisdiction.USFederal]: 'US Federal',
  [ProceduralJurisdiction.Other]: 'Other',
};

function defaultJurisdiction(): ProceduralJurisdiction {
  switch (getFrontendURLRegion()) {
    case 'us':
      return ProceduralJurisdiction.USFederal;
    case 'au':
      return ProceduralJurisdiction.Other;
    case 'uk':
    case 'staging':
    default:
      return ProceduralJurisdiction.EnglandAndWales;
  }
}

function getBlockedReason(eligibility: UploadEligibilityResponse | undefined): string | null {
  if (!eligibility || eligibility.canUpload) return null;
  if (eligibility.blockedBy === 'extraction_in_flight') {
    return 'A timetable is currently being processed. Please wait for it to finish before uploading another.';
  }
  if (eligibility.blockedBy === 'pending_suggestions') {
    const count = eligibility.pendingSuggestionCount;
    return `Resolve the ${count} pending suggestion${count === 1 ? '' : 's'} before uploading another timetable.`;
  }
  return null;
}

interface UploadTimetableButtonProps {
  matterId: string;
}

export function UploadTimetableButton({ matterId }: UploadTimetableButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [timeZone, setTimeZone] = useState<string>(() => detectTimeZone());
  const [jurisdiction, setJurisdiction] = useState<ProceduralJurisdiction>(() => defaultJurisdiction());
  const timeZoneOptions = useMemo(() => getTimeZoneOptions(), []);

  const { data: eligibility, isLoading: isLoadingEligibility } = useGetProceduralTimelineUploadEligibility(matterId);
  const { data: existingEvents } = useGetProceduralTimelineEvents(matterId);
  const hasExistingEvents = (existingEvents?.length ?? 0) > 0;

  const stageFile = useStageProceduralFile();
  const submitFile = useSubmitProceduralFile();
  const deleteStagedFile = useDeleteStagedProceduralFile();

  const stagedUploadId = stageFile.data?.uploadId ?? null;
  const isStaging = stageFile.isLoading;
  const isSubmitting = submitFile.isLoading;
  const isBusy = isStaging || isSubmitting;

  const blockedReason = getBlockedReason(eligibility);

  const isDisabled = isLoadingEligibility || !eligibility?.canUpload;

  const handleClick = () => inputRef.current?.click();

  const clearLocalState = () => {
    setSelectedFile(null);
    stageFile.reset();
    submitFile.reset();
  };

  // Cancel paths: discard the temp S3 object if staging succeeded. If staging is still
  // in flight or failed, there's nothing confirmed in S3 yet — lifecycle reaps anything
  // stray. Use clearLocalState directly after a successful commit (temp already moved).
  const cancelAndClear = () => {
    if (stagedUploadId && selectedFile) {
      deleteStagedFile.mutate({ matterId, uploadId: stagedUploadId, filename: selectedFile.name });
    }
    clearLocalState();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_PROCEDURAL_FILE_SIZE_BYTES) {
      toast.error(`File is too large. Maximum size is ${formatBytes(MAX_PROCEDURAL_FILE_SIZE_BYTES)}.`);
      return;
    }
    if (
      file.type &&
      !ALLOWED_PROCEDURAL_MIME_TYPES.includes(file.type as (typeof ALLOWED_PROCEDURAL_MIME_TYPES)[number])
    ) {
      toast.error('Unsupported file type. Please upload a PDF, DOC, or DOCX file.');
      return;
    }

    setSelectedFile(file);
    stageFile.mutate({ matterId, file });
  };

  const handleSubmit = async () => {
    if (!selectedFile || !stagedUploadId) return;
    try {
      await submitFile.mutateAsync({
        matterId,
        uploadId: stagedUploadId,
        name: selectedFile.name,
        mimeType: selectedFile.type,
        size: selectedFile.size,
        timeZone,
        jurisdiction,
      });
      toast.success('Timetable uploaded. Extraction will begin shortly.');
      clearLocalState();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload timetable.';
      toast.error(message);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (isBusy) return;
    if (!open) cancelAndClear();
  };

  const button = (
    <Button variant="primary" onClick={handleClick} disabled={isDisabled}>
      <Icon className="lucide--upload" />
      Extract From Timetable
    </Button>
  );

  const stageError = stageFile.error;

  const submitLabel = isSubmitting ? 'Submitting...' : isStaging ? 'Uploading...' : 'Submit';

  return (
    <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
      <>
        <input ref={inputRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={handleFileSelected} />

        {blockedReason && isDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>{button}</span>
            </TooltipTrigger>
            <TooltipContent>{blockedReason}</TooltipContent>
          </Tooltip>
        ) : (
          button
        )}

        <Dialog open={selectedFile !== null} onOpenChange={handleOpenChange}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Procedural Timetable</DialogTitle>
              <DialogDescription>
                Autimatically extract procedural events from this document and propose them as suggestions for review.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              {selectedFile && (
                <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
                  <Icon className="lucide--file-text text-gray-500" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-800">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatBytes(selectedFile.size)}</p>
                  </div>
                  {isStaging && <Icon className="lucide--loader-2 animate-spin text-sky-600" />}
                  {stagedUploadId && !isSubmitting && <Icon className="lucide--check-circle-2 text-teal-600" />}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="procedural-timezone" className="mb-0 text-xs font-medium text-gray-700">
                  Time zone
                </Label>
                <p className="text-xs text-gray-500">Used to resolve dates the document expresses in local time.</p>
                <SearchableCombobox
                  options={timeZoneOptions}
                  value={timeZone}
                  onChange={(v) => setTimeZone(v || detectTimeZone())}
                  placeholder="Select time zone"
                  searchPlaceholder="Search time zones..."
                  emptyText="No time zones found."
                  containerClassName="flex w-full items-center gap-2 [&>span]:w-full"
                  triggerClassName="w-full justify-between font-normal"
                  clearable={false}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="procedural-jurisdiction" className="mb-0 text-xs font-medium text-gray-700">
                  Jurisdiction
                </Label>
                <p className="text-xs text-gray-500">
                  Used to calculate court-day deadlines according to jurisdiction specific rules.
                </p>
                <Select value={jurisdiction} onValueChange={(v) => setJurisdiction(v as ProceduralJurisdiction)}>
                  <SelectTrigger id="procedural-jurisdiction" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.values(ProceduralJurisdiction) as ProceduralJurisdiction[]).map((j) => (
                      <SelectItem key={j} value={j}>
                        {JURISDICTION_LABELS[j]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {jurisdiction === ProceduralJurisdiction.Other && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
                    <p>
                      Automatic deadline calculations are only available for supported jurisdictions. Events will still
                      be extracted, but timeline rules may not be applied correctly. If you'd like us to add support for
                      your jurisdiction, email{' '}
                      <a href="mailto:support@wexler.ai" className="font-medium underline">
                        support@wexler.ai
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>

              {hasExistingEvents && (
                <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                  <Icon className="lucide--info mt-0.5 shrink-0 text-sky-600" />
                  <p>
                    Extracted events will be compared against your existing events, and where appropriate updates will
                    be suggested for review rather than creating duplicates.
                  </p>
                </div>
              )}

              {stageError && (
                <p className="text-sm text-red-600">
                  Upload failed: {stageError.message}. Close this dialog and try again.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button onClick={cancelAndClear} disabled={isBusy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={isBusy || !stagedUploadId || !!stageError}>
                {submitLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    </PermissionGate>
  );
}
